'use client';

import { useLocationStore } from '@ahm/react-map/store';
import { Avatar, Button, rem } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { ArrowRight } from 'lucide-react';
import { usePathname } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';
import classes from '../booking/components/paths/path-btn.module.css';
import { useQuickAccessAIChatStore } from './quick-access-store';
import { assetPrefix } from '@/lib/constants';
import { formatChatBotData, getServiceType } from '@/lib/utils';
import { useBookingStore } from '@/stores/booking/booking-store-provider';

type ButtonVariant = 'card' | 'button';

interface QuickAccessAIChatProps {
  variant?: ButtonVariant;
  fullWidth?: boolean;
  radius?: string | number;
  color?: string;
  text?: string | React.ReactNode;
  rightSection?: React.ReactNode;
  withShadow?: boolean;
  border?: string;
  styles?: Record<string, unknown>;
  className?: string;
  style?: React.CSSProperties;
  classNames?: Record<string, string>;
}

/**
 * QuickAccessAIChat - A component for accessing AI chat functionality
 * Supports both card and button variants in a single component
 */
export default function QuickAccessAIChat({
  variant = 'card',
  fullWidth,
  radius,
  color = 'purple',
  text = 'Lên đơn với AI',
  rightSection,
  withShadow = true,
  border,
  styles: customStyles,
  className,
  style,
  classNames,
}: QuickAccessAIChatProps) {
  const pathname = usePathname();

  const { isBikeService } = useMemo(() => getServiceType(pathname), [pathname]);
  const initialData = useQuickAccessAIChatStore((state) => state.initialData);
  const setInitialData = useQuickAccessAIChatStore(
    (state) => state.setInitialData
  );
  const isMobile = useMediaQuery('(max-width: 767px)');
  const paths = useLocationStore.use.paths();
  const {
    orderTime,
    bikePackageWeight,
    bikePackageSize,
    serviceId,
    itemDescriptions,
    serviceTypes,
  } = useBookingStore((state) => state);

  // Determine props based on variant
  const buttonConfig = useMemo(() => {
    const defaultConfig = {
      color,
      variant: 'subtle',
      border: border ?? 'none',

      style: {
        backgroundColor: '#F3F0FF',
      },
      leftSection: (
        <Avatar src={`${assetPrefix}/static/icons/AI/Sparkle.svg`} size={16} />
      ),
    };

    if (variant === 'card') {
      return {
        ...defaultConfig,
        px: 'md',
        size: 'md',
        height: '100%',
        radius: radius ?? '0px 0px 12px 12px',
        justify: 'space-between' as const,
        fullWidth: fullWidth ?? true,
        className,
        classNames: { ...classes, ...classNames },
        styles: {
          root: {
            ...(withShadow && {
              boxShadow: 'rgba(0, 0, 0, 0.1) 0px 2px 4px 0px',
            }),
            ...style,
          },
          label: {
            width: '100%',
            paddingRight: rem(6),
            paddingLeft: rem(6),
          },
          ...(customStyles ?? {}),
        },
        rightSection: rightSection ?? (
          <ArrowRight className="size-4" color="black" />
        ),
      };
    }

    return {
      ...defaultConfig,
      radius: radius ?? (isMobile ? 'none' : 'xl'),
      size: 'sm',
      px: rem(12),
      py: rem(10),
      h: 'auto',
      justify: 'flex-start' as const,
      fullWidth: fullWidth ?? isMobile,
      className,
      classNames,
      styles: {
        root: {
          border: border ?? 'none',
          ...(withShadow && {
            boxShadow: 'rgba(0, 0, 0, 0.1) 0px 2px 4px 0px',
          }),
          ...style,
        },
        ...customStyles,
      },
    };
  }, [
    color,
    variant,
    radius,
    isMobile,
    border,
    fullWidth,
    customStyles,
    withShadow,
    rightSection,
    className,
    classNames,
    style,
  ]);

  // Helper function to compare data objects
  const isDataChanged = useMemo(() => {
    if (!initialData) return true; // If no initial data, enable button

    const currentData = formatChatBotData(
      paths,
      bikePackageSize,
      bikePackageWeight,
      itemDescriptions,
      serviceId,
      orderTime,
      serviceTypes
    );

    // Deep comparison of relevant data
    return JSON.stringify(initialData) !== JSON.stringify(currentData);
  }, [
    paths,
    bikePackageSize,
    bikePackageWeight,
    itemDescriptions,
    serviceId,
    orderTime,
    serviceTypes,
    initialData,
  ]);

  // Reset initial data when no relevant data exists
  useEffect(() => {
    const hasRelevantData =
      paths?.length ?? bikePackageSize ?? bikePackageWeight ?? serviceId;

    if (!hasRelevantData && initialData) {
      setInitialData(null);
    }
  }, [
    paths,
    bikePackageSize,
    bikePackageWeight,
    serviceId,
    initialData,
    setInitialData,
  ]);

  // Chat initialization logic
  const [chatInit, setChatInit] = useState(false);
  useEffect(() => {
    const interval = setInterval(() => {
      if (window.Chat?.initialized) {
        setChatInit(true);
        clearInterval(interval);
      }
    }, 300);
    return () => clearInterval(interval);
  }, []);

  if (!chatInit) {
    return null;
  }

  const handleClick = () => {
    const hasRelevantData =
      paths?.length ?? bikePackageSize ?? bikePackageWeight ?? serviceId;

    if (!hasRelevantData) {
      window.Chat.RPCs?.togglePanel();
      return;
    }

    const formatDataToChatBot = formatChatBotData(
      paths,
      bikePackageSize,
      bikePackageWeight,
      itemDescriptions,
      serviceId,
      orderTime,
      serviceTypes
    );

    // Save initial data if this is the first time sending data
    if (!initialData) {
      setInitialData(formatDataToChatBot);
    }

    // Send relevant data when available
    window.Chat.RPCs.receiveDataFromParent({
      ...formatDataToChatBot,
    });
  };
  if (!isBikeService) {
    return null;
  }

  // Hide button if there's initial data but no changes
  if (initialData && !isDataChanged) {
    return null;
  }

  return (
    <Button {...buttonConfig} onClick={handleClick}>
      {text}
    </Button>
  );
}

/**
 * Legacy component for backward compatibility - Card variant
 */
export function QuickAccessAIChatCard() {
  return <QuickAccessAIChat variant="card" />;
}

/**
 * Legacy component for backward compatibility - Button variant
 */
type Props = {
  fullWidth?: boolean;
  radius?: string | number;
  text?: string | React.ReactNode;
  border?: string;
  withShadow?: boolean;
  className?: string;
  style?: React.CSSProperties;
  classNames?: Record<string, string>;
  styles?: Record<string, unknown>;
};
export function QuickAccessAIChatButton(props: Props) {
  return <QuickAccessAIChat variant="button" {...props} />;
}
